import { ServiceAdministrationService } from './../../../service-administration/service/service-administration.service';
import { PartyServices } from './../../../service-administration/model/party-services.model';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Region } from '../../../../model/region.model';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { buffer, debounceTime, distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import { EnrollmentPeriods } from '../model/enrollment-periods.model';
import { Grade } from 'src/app/model/school.model';

@Component({
    selector: 'service-dropdown',
    templateUrl: './service-dropdown.component.html',
    styleUrls: ['./service-dropdown.component.scss']
})
export class ServiceDropdownComponent implements OnInit {
    @Input() label: string = '';
    @Input() isMultiple: boolean = false;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() region: Region;
    @Input() enrollmentPeriods: EnrollmentPeriods[] = [];
    @Input() activeOnly: boolean = false;
    @Input() selectServices: PartyServices[] = [];
    @Input() grades: Grade[] = [];
    previousRegion: Region;
    previousEnrollmentPeriods: EnrollmentPeriods[] = [];
    previousGrades: Grade[] = [];
    selectServicesProcessing: boolean = false;

    @Input('reset') reset: boolean;
    @Output() serviceEvent = new EventEmitter<PartyServices[]>();
    @Output() selectServicesChange = new EventEmitter<PartyServices[]>();

    partyServices: PartyServices[] = [];
    filteredPartyServices: PartyServices[] = [];

    form: UntypedFormGroup;
    @ViewChild(MatSelect) matSelect: MatSelect;
    @ViewChild('filterRef') filterRef: ElementRef;

    subs: Subscription[] = [];

    isLoading: boolean = false;

    burstMS: number = 500;
    private changes$ = new Subject<any | null>();
    private destroy$ = new Subject<void>();
    private lastEmitted: any = undefined;

    constructor(
        private serviceAdministrationService: ServiceAdministrationService,
        private fb: UntypedFormBuilder
    ) {
        this.form = this.fb.group({
            serviceSelect: [[]],
            filter: ['']
        });

        this.subscribers();

        const burstEnd$ = this.changes$.pipe(debounceTime(this.burstMS));

        this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((values) => {
            if (!values.length) return;

            //console.warn('[ServicesInput] Burst buffer contents:', values);

            const preferred = [...values].reverse().find((v) => this.isNonEmpty(v)) ?? values[values.length - 1];

            //console.warn('[ServicesInput] Chosen value:', preferred);

            this.emitNormalized(preferred);
        });
    }

    private isNonEmpty(value: any): boolean {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    }

    private emitNormalized(value: any) {
        let normalized: any;

        if (Array.isArray(value)) {
            normalized = value.map((r: any) => r.ID);
        } else if (value && typeof value === 'object') {
            normalized = value.ID;
        } else {
            normalized = value;
        }

        if (this.isSame(normalized, this.lastEmitted)) {
            //console.warn('[CountyInput] Skipping emit (no change):', normalized);
            return;
        }

        //console.warn('[CountyInput] Emitting:', normalized);

        this.lastEmitted = normalized;

        this.onOpen(true);
    }

    private isSame(a: any, b: any): boolean {
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }

        return a === b;
    }

    ngOnInit(): void {}

    subscribers() {
        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredPartyServices = this.partyServices;
                //this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            } else {
                this.filteredPartyServices = this.partyServices.filter(
                    (partyService) =>
                        partyService.SERVICE_CODE!.toString().toLowerCase().includes(value.toLowerCase()) ||
                        partyService.SERVICE_NAME!.toLowerCase().includes(value.toLowerCase())
                );
            }
        });
        this.subs.push(filterInputSub);

        let serviceSelectSub = this.serviceSelect.valueChanges.subscribe((partyServices: PartyServices[]) => {
            this.serviceEvent.emit(partyServices);
        });
        this.subs.push(serviceSelectSub);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.reset) {
            this.resetFunction();
        }

        if (this.disabled) {
            this.serviceSelect.disable();
        } else {
            this.serviceSelect.enable();
        }

        if (
            (changes['enrollmentPeriods'] && changes['enrollmentPeriods'].currentValue) ||
            (changes['grades'] && changes['grades'].currentValue) ||
            (changes['selectServices'] && changes['selectServices'].currentValue)
        ) {
            if (changes['selectServices'] && changes['selectServices'].currentValue) {
                if (!this.selectServicesProcessing || !this.isLoading) {
                    this.selectServicesProcessing = true;
                    this.changes$.next(changes['selectServices'].currentValue);
                    //console.warn(changes['selectServices']);
                }
            }
        }
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });

        this.destroy$.next();
        this.destroy$.complete();
    }

    getPartyServices() {
        if (this.region) {
            this.isLoading = true;

            let payload = this.generatePayload();

            this.serviceAdministrationService.index(payload).subscribe((res) => {
                if (res.partyServices) {
                    let previouslyChosenPartyServices = this.isMultiple ? this.serviceSelect.value : [this.serviceSelect.value];
                    //this.partyServices = res.partyServices.sort((a: any, b: any) => a.SERVICE_NAME!.localeCompare(b.SERVICE_NAME!));
                    this.partyServices = res.partyServices.sort((n1: any, n2: any) => (n1.SERVICE_CODE! > n2.SERVICE_CODE! ? 1 : -1));
                    this.filteredPartyServices = this.partyServices;

                    if (this.selectServicesProcessing) {
                        this.select(this.selectServices);
                        this.selectServices = [];
                        this.selectServicesProcessing = false;
                    } else {
                        this.select(previouslyChosenPartyServices);
                    }
                } else {
                }

                this.isLoading = false;
            });
        } else {
            this.partyServices = [];
            this.filteredPartyServices = [];
        }
    }

    generatePayload() {
        // regionId: payload.regionId,
        // activeServicesOnly: payload.activeServicesOnly,
        // disabledServicesOnly: payload.disabledServicesOnly,
        // mpoServicesOnly: payload.mpoServicesOnly,
        // enrollmentPeriods: payload.enrollmentPeriods
        let payload: any = {};

        payload.regionId = this.region?.ID;
        payload.activeServicesOnly = this.activeOnly;
        if (this.enrollmentPeriods && this.enrollmentPeriods.length > 0 && this.enrollmentPeriods[0]) {
            payload.enrollmentPeriods = this.enrollmentPeriods.map((ep) => ep.ID);
        }
        if (this.grades && this.grades.length > 0 && this.grades[0]) {
            payload.grades = this.grades.map((g) => g.ID);
        }

        return payload;
    }

    select(partyServices: PartyServices[]) {
        if (partyServices && partyServices.length > 0) {
            let foundServices: PartyServices[] = [];

            partyServices.forEach((ps) => {
                let foundService = this.filteredPartyServices.find((fps) => fps.ID === ps.ID);
                if (foundService) {
                    foundServices.push(foundService);
                }
            });

            if (foundServices.length > 0) {
                if (this.isMultiple) {
                    this.serviceSelect.patchValue(foundServices);
                } else {
                    this.serviceSelect.patchValue(foundServices[0]);
                }
            } else {
                this.serviceSelect.patchValue([]);
            }
        } else {
            this.serviceSelect.patchValue([]);
        }
    }

    selectAll(type?: string) {
        if (this.isMultiple) {
            if (type === 'Active') {
                this.serviceSelect.patchValue(this.filteredPartyServices.filter((ps) => ps.PARTY_SERVICE_STATUS_TYPES_ID === 1));
            } else if (type === 'Inactive') {
                this.serviceSelect.patchValue(this.filteredPartyServices.filter((ps) => ps.PARTY_SERVICE_STATUS_TYPES_ID === 2));
            } else {
                this.serviceSelect.patchValue(this.filteredPartyServices);
            }
        }
    }

    deSelectAll() {
        this.reset = false;
        this.serviceSelect.patchValue([]);

        if (!this.isMultiple) {
            this.close();
        }
    }

    close() {
        this.matSelect.close();
    }

    resetFunction() {
        this.serviceSelect.setValue([]);
        this.serviceEvent.emit([]);
        this.reset = false;
    }

    get serviceSelect(): any {
        return this.form.get('serviceSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.form.get('filter');
    }

    onOpen(opened: boolean) {
        if (opened) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;

            if (
                this.previousRegion?.ID === this.region?.ID &&
                JSON.stringify(this.enrollmentPeriods.sort()) === JSON.stringify(this.previousEnrollmentPeriods.sort()) &&
                JSON.stringify(this.grades.sort()) === JSON.stringify(this.previousGrades.sort())
            ) {
            } else {
                this.filterInput?.patchValue('');
                this.getPartyServices();
            }
            this.previousRegion = this.region;
            this.previousEnrollmentPeriods = this.enrollmentPeriods;
            this.previousGrades = this.grades;
        } else {
        }
    }
}
